import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MailOpen } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import { AvatarView } from '../../components/ui/AvatarView';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { SyncButton } from '../../components/ui/SyncButton';
import { Toast } from '../../components/ui/Toast';

// The invitee-facing side of an owner "add": channels someone invited this
// account to, each accepted (become a real member) or declined here. See
// backend/workflows/channel/invite_list.wsl / invite_accept.wsl and
// StoreContext's channelInvites / acceptInvite / declineInvite.
export function ChannelInvitesScreen() {
  const navigate = useNavigate();
  const { channelInvites, acceptInvite, declineInvite, syncChannelInvites, manualRefresh } = useStore();
  const [busyNickname, setBusyNickname] = useState<string | null>(null);
  const [error, setError] = useState('');

  const onAccept = async (nickname: string, channelId: string) => {
    setBusyNickname(nickname);
    setError('');
    try {
      await acceptInvite(nickname);
      navigate(`/channels/${channelId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to accept this invite.');
      setBusyNickname(null);
    }
  };

  const onDecline = async (nickname: string) => {
    setBusyNickname(nickname);
    setError('');
    try {
      await declineInvite(nickname);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to decline this invite.');
    } finally {
      setBusyNickname(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader
        title="Invitations"
        action={manualRefresh ? <SyncButton onSync={syncChannelInvites} ariaLabel="Refresh invitations" /> : undefined}
      />

      {error && <Toast message={error} onDismiss={() => setError('')} />}

      {channelInvites.length === 0 ? (
        <EmptyState
          icon={MailOpen}
          title="No pending invitations"
          subtitle="When someone invites you to a channel, it shows up here."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {channelInvites.map(({ channel, invitedByName, invitedByNickname }) => {
            const inviter = invitedByName || invitedByNickname;
            const busy = busyNickname === channel.nickname;
            return (
              <AppCard key={channel.nickname} className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <AvatarView
                    symbol={channel.avatarSymbol}
                    colorHex={channel.accentHex}
                    imageKey={channel.avatarImageKey}
                    size={48}
                  />
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <span className="font-semibold truncate">{channel.title}</span>
                    <span className="text-sm text-black/50 dark:text-white/50 truncate">
                      {channel.nickname}
                      {inviter ? ` · invited by ${inviter}` : ''}
                    </span>
                  </div>
                </div>
                {channel.description && (
                  <p className="text-sm text-black/60 dark:text-white/60 line-clamp-2 m-0">{channel.description}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={() => onAccept(channel.nickname, channel.id)}
                    disabled={busy}
                    className="flex-1"
                  >
                    {busy ? 'Accepting...' : 'Accept'}
                  </Button>
                  <Button
                    variant="bordered"
                    onClick={() => onDecline(channel.nickname)}
                    disabled={busy}
                    className="flex-1"
                  >
                    Decline
                  </Button>
                </div>
              </AppCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
